import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { CloudArrowUpIcon, DocumentIcon } from '@heroicons/react/24/outline';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'; // Test key
const RECAPTCHA_BYPASS = process.env.REACT_APP_BYPASS_RECAPTCHA === 'true';

const UploadPage: React.FC = () => {
  const navigate = useNavigate();
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  
  const [formData, setFormData] = useState({
    speakerName: '',
    speakerEmail: '',
    presentationTitle: '',
  });
  
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [uploadLink, setUploadLink] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fl = e.target.files;
    if (!fl || fl.length === 0) return;
    const selected: File[] = Array.from(fl);
    const allowed = ['.pdf', '.pptx', '.pps', '.mp4', '.zip'];
    for (const f of selected) {
      const ext = f.name.substring(f.name.lastIndexOf('.')).toLowerCase();
      if (!allowed.includes(ext)) {
        setError('Only PDF, PPTX, PPS, MP4, ZIP files are allowed');
        setFiles([]);
        return;
      }
      if (f.size > 250 * 1024 * 1024) {
        setError(`File ${f.name} exceeds 250MB limit`);
        setFiles([]);
        return;
      }
    }
    setFiles(selected);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!files || files.length === 0) {
      setError('Please select at least one file');
      return;
    }
    
    // Get reCAPTCHA token (bypass in dev/e2e)
    let recaptchaToken: string | null | undefined = null;
    if (RECAPTCHA_BYPASS) {
      recaptchaToken = 'bypass';
    } else {
      try {
        recaptchaToken = await recaptchaRef.current?.executeAsync();
      } catch (e) {
        setError('reCAPTCHA verification failed. Please try again.');
        return;
      } finally {
        recaptchaRef.current?.reset();
      }
      if (!recaptchaToken) {
        setError('reCAPTCHA verification failed. Please try again.');
        return;
      }
    }
    
    setLoading(true);
    
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('speaker_name', formData.speakerName);
      formDataToSend.append('speaker_email', formData.speakerEmail);
      formDataToSend.append('presentation_title', formData.presentationTitle);
      for (const f of files) formDataToSend.append('files', f);
      formDataToSend.append('recaptcha_token', recaptchaToken);

      const response = await axios.post(`${API_BASE_URL}/api/upload`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (p) => {
          if (p.total) {
            setUploadProgress(Math.round((p.loaded * 100) / p.total));
          }
        },
      });

      setSuccess(true);
      setUploadLink(response.data.upload_link);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'An error occurred during upload');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (uploadLink) {
      const fullLink = `${window.location.origin}/update/${uploadLink}`;
      navigator.clipboard
        .writeText(fullLink)
        .then(() => alert('Link copied to clipboard!'))
        .catch(() => alert('Could not copy link. Please copy it manually.'));
    }
  };

  if (success && uploadLink) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-2xl w-full bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
              <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Upload Successful!</h2>
            <p className="text-gray-600 mb-4">
              Your presentation has been uploaded successfully.
            </p>
            
            <div className="bg-amber-50 border-2 border-amber-400 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <svg className="h-6 w-6 text-amber-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <div>
                  <p className="font-bold text-amber-900 mb-2">IMPORTANT: Save This Link!</p>
                  <p className="text-sm text-amber-800 mb-2">
                    <strong>Use this unique link for any future updates to your presentation.</strong> Do NOT upload from the main page again, as this will create a separate entry instead of updating your existing presentation.
                  </p>
                  <p className="text-sm text-amber-800">
                    Bookmark this link or save it in a safe place. You will need it if you want to upload a new version of your presentation.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-600 mb-2">Your update link:</p>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={`${window.location.origin}/update/${uploadLink}`}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm"
                />
                <button
                  onClick={copyToClipboard}
                  className="btn-primary whitespace-nowrap"
                >
                  Copy Link
                </button>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm text-gray-600 mb-2">Uploaded files in this version:</p>
              <ul className="list-disc pl-5 text-sm text-gray-800">
                {files.map((f) => (
                  <li key={f.name}>{f.name}</li>
                ))}
              </ul>
            </div>
            
            <button
              onClick={() => {
                setSuccess(false);
                setUploadLink(null);
                setFormData({ speakerName: '', speakerEmail: '', presentationTitle: '' });
                setFiles([]);
              }}
              className="btn-outline"
            >
              Upload Another Presentation
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-[#00529B] to-[#E31D3C] bg-clip-text text-transparent">
            Language Intelligence Conference
          </h1>
          <h2 className="text-2xl font-semibold text-gray-800 mb-2">
            Presentation Collection
          </h2>
          <p className="text-gray-600">
            Upload your presentation for the conference
          </p>
        </div>

        {/* Main Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Speaker Name */}
            <div>
              <label htmlFor="speakerName" className="block text-sm font-semibold text-gray-700 mb-2">
                Speaker Name *
              </label>
              <input
                type="text"
                id="speakerName"
                name="speakerName"
                required
                value={formData.speakerName}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00529B] focus:border-transparent"
                placeholder="Enter your full name"
              />
            </div>

            {/* Speaker Email */}
            <div>
              <label htmlFor="speakerEmail" className="block text-sm font-semibold text-gray-700 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                id="speakerEmail"
                name="speakerEmail"
                required
                value={formData.speakerEmail}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00529B] focus:border-transparent"
                placeholder="your.email@example.com"
              />
            </div>

            {/* Presentation Title */}
            <div>
              <label htmlFor="presentationTitle" className="block text-sm font-semibold text-gray-700 mb-2">
                Presentation Title *
              </label>
              <input
                type="text"
                id="presentationTitle"
                name="presentationTitle"
                required
                value={formData.presentationTitle}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00529B] focus:border-transparent"
                placeholder="Enter your presentation title"
              />
            </div>

            {/* File Upload */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Upload Presentation Files (PDF, PPTX, PPS, MP4, ZIP) *
              </label>
              <div className="mt-2 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-lg hover:border-[#00529B] transition-colors">
                <div className="space-y-2 text-center">
                  {files.length > 0 ? (
                    <div className="text-left">
                      <div className="flex items-center justify-center gap-2 text-[#00529B] mb-2">
                        <DocumentIcon className="h-8 w-8" />
                        <span className="font-medium">{files.length} file(s) selected</span>
                      </div>
                      <div className="max-h-36 overflow-auto text-sm">
                        {files.map((f) => (
                          <div key={f.name} className="text-gray-700">{f.name}</div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <CloudArrowUpIcon className="mx-auto h-12 w-12 text-gray-400" />
                  )}
                  <div className="flex text-sm text-gray-600">
                    <label
                      htmlFor="file-upload"
                      className="relative cursor-pointer bg-white rounded-md font-medium text-[#00529B] hover:text-[#003D74] focus-within:outline-none"
                    >
                      <span>{files.length > 0 ? 'Change files' : 'Upload files'}</span>
                      <input
                        id="file-upload"
                        name="file-upload"
                        type="file"
                        accept=".pdf,.pptx,.pps,.mp4,.zip"
                        multiple
                        required
                        onChange={handleFileChange}
                        className="sr-only"
                      />
                    </label>
                    <p className="pl-1">or drag and drop</p>
                  </div>
                  <p className="text-xs text-gray-500">Allowed: PDF, PPTX, PPS, MP4, ZIP. Max 250MB per file.</p>
                  {loading && (
                    <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                      <div className="bg-[#00529B] h-2 rounded-full" style={{ width: `${uploadProgress}%` }} />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Important Notice */}
            <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-amber-700 font-semibold mb-1">
                    Important: Conference Equipment Only
                  </p>
                  <p className="text-sm text-amber-700">
                    Only conference-provided equipment is allowed. There's no possibility to plugin your own computer into the conference room.
                  </p>
                </div>
              </div>
            </div>

            {/* reCAPTCHA */}
            <div className="flex justify-center">
              <ReCAPTCHA
                ref={recaptchaRef}
                size="invisible"
                sitekey={RECAPTCHA_SITE_KEY}
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary text-lg py-4 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Uploading...
                </span>
              ) : (
                'Upload Presentation'
              )}
            </button>
          </form>
        </div>

        {/* Data Protection Notice */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">
            Publication / Data Protection
          </h3>
          <p className="text-sm text-gray-600 leading-relaxed">
            Presentations will be published after the conference on the conference website. Please do not include in your
            presentation elements you do not wish to see published. In addition, speakers and attendance will be photographed.
            In submitting a speaker's proposal, you agree that your data may be published on the conference website, via our
            promotional channels and that we may use it for promotional purposes.
          </p>
        </div>
      </div>
    </div>
  );
};

export default UploadPage;
